<template>
  <div class="deck-progress">
    <div class="holder-result">
      <div class="result" :class="{ 'checkpoint-result': onCheckpoint }">
        <div v-if="onCheckpoint" class="checkpoint">
          <p v-if="!reviewCompleted" class="title">
            Checkpoint!
          </p>
          <p v-else class="title">
            Your Progress:
          </p>
          <p v-if="!reviewCompleted && studyScore.score > 0 && studyScore.score < 100" class="subtitle">
            Check how well you’ve done with this deck so far.
          </p>
          <p v-else-if="!reviewCompleted && studyScore.score === 100" class="subtitle study-complete">
            <span>Great work!</span>
            <span>You’ve answered all questions in this deck.</span>
          </p>
          <p v-else class="subtitle">
            You have reviewed {{ studyScore.reviewedCount }} out of {{ deck.cardCount }} cards in this deck.
          </p>
        </div>
        <p v-else class="title">
          Deck Progress:
        </p>
        <div v-if="!review" class="answers desktop">
          <div>
            <span>Known cards:</span>
            <span class="correct">{{ studyScore.knownCount }}</span>
          </div>
          <div>
            <span>Still Learning cards:</span>
            <span class="wrong">{{ studyScore.unknownCount }}</span>
          </div>
          <div>
            <span>Skipped cards:</span>
            <span class="skipped">{{ studyScore.notSureCount }}</span>
          </div>
        </div>
        <div v-else class="answers desktop">
          <div>
            <span>Reviewed cards:</span>
            <span class="correct">{{ studyScore.reviewedCount }}</span>
          </div>
          <div>
            <span>Remaining cards:</span>
            <span class="wrong">{{ studyScore.notReviewedCount }}</span>
          </div>
        </div>
      </div>

      <div class="progress-holder">
        <div class="progress">
          <Chart
            id="deck-progress"
            slot="chart"
            type="doughnut"
            :data="chartData"
            :options="chartOptions"
          />
          <div class="number">
            <p>{{ currentChartData[0] }}%</p>
            <p>{{ currentChartData[1] }}</p>
          </div>
        </div>
      </div>

      <div v-if="!reviewCompleted && !reviewProgress" class="answers mobile">
        <div>
          <span class="known-cards">Known cards:</span>
          <span class="correct">{{ studyScore.knownCount }}</span>
        </div>
        <div>
          <span>Still Learning cards:</span>
          <span class="wrong">{{ studyScore.unknownCount }}</span>
        </div>
        <div>
          <span>Skipped cards:</span>
          <span class="skipped">{{ studyScore.notSureCount }}</span>
        </div>
      </div>
      <div v-else class="answers mobile">
        <div>
          <span>Reviewed cards:</span>
          <span class="correct">{{ studyScore.reviewedCount }}</span>
        </div>
        <div>
          <span>Remaining cards:</span>
          <span class="wrong">{{ studyScore.notReviewedCount }}</span>
        </div>
      </div>
    </div>
    <div v-if="!onCheckpoint" class="btn-holder">
      <button class="btn-text" :disabled="!canRotate" @click="$emit('rotate')">
        Exam Result
      </button>
    </div>
  </div>
</template>

<script>
import Chart from '../../components/Chart';

export default {
  components: {
    Chart,
  },
  props: {
    studyScore: { type: Object, required: true, default: () => ({}) },
    deck: { type: Object, required: false, default: () => ({}) },
    canRotate: { type: Boolean, required: false },
    reviewCompleted: Boolean,
    reviewProgress: Boolean,
  },
  data() {
    return {
      chartData: {
        datasets: [
          {
            label: 'Dataset 1',
            data: [this.studyScore.notSureCount, this.studyScore.knownCount, this.studyScore.unknownCount, this.studyScore.reviewedCount, this.studyScore.notReviewedCount],
            backgroundColor: ['#ABAFB5', '#56BFC7', '#FF5050', '#56BFC7', '#FF5050'],
          },
        ],
      },
      chartOptions: {
        responsive: true,
        borderRadius: (this.studyScore.notSureCount === 0 && this.studyScore.knownCount === 0) || (this.studyScore.notSureCount === 0 && this.studyScore.unknownCount === 0) || (this.studyScore.knownCount === 0 && this.studyScore.unknownCount === 0) || this.studyScore.reviewedCount === 0 || this.studyScore.notReviewedCount === 0 ? 0 : 40,
        backgroundColor: 'red',
        borderWidth: 0,
        spacing: (this.studyScore.notSureCount === 0 && this.studyScore.knownCount === 0) || (this.studyScore.notSureCount === 0 && this.studyScore.unknownCount === 0) || (this.studyScore.knownCount === 0 && this.studyScore.unknownCount === 0) || this.studyScore.reviewedCount === 0 || this.studyScore.notReviewedCount === 0 ? 0 : 0.5,
        cutout: this.$store.state.layout === 'desktop' ? 90 : 70,
        borderAlign: 'inner',
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
      review: this.$route.query.review,
    };
  },
  computed: {
    onCheckpoint() {
      return this.$route.path.includes('/checkpoint/');
    },
    currentChartData() {
      const reviewScore = this.reviewCompleted ? 100 : this.studyScore.score;

      return this.reviewCompleted || this.reviewProgress ? [reviewScore, 'Reviewed'] : [this.studyScore.score, 'Knowledge'];
    },
  },
};
</script>

<style lang="scss">
.deck-progress {
  padding: var(--spacing-30) var(--spacing-50);
  border: 1px solid var(--accent);
  border-radius: 10px;

  .holder-result {
    display: flex;
    justify-content: space-between;

    .title {
      margin-top: 0;
      font-size: 32px;
      font-weight: 500;
      line-height: 110%;
      margin-bottom: var(--spacing-40);
    }

    .result {
      max-width: 360px;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-right: 0;

      .checkpoint {
        .title {
          margin-bottom: 0;
        }

        .subtitle {
          margin: var(--spacing-12) 0 var(--spacing-40);
          font-size: 16px;
          line-height: 110%;
          color: var(--main-dark-light-3);

          &.study-complete {
            span:first-of-type {
              display: block;
              font-weight: 600;
            }
          }
        }
      }
    }

    .answers {
      margin-right: 70px;

      &.mobile {
        display: none;
      }

      div {
        display: flex;
        justify-content: space-between;
        margin-bottom: var(--spacing-16);

        span {
          font-size: 18px;
          font-weight: 500;
          line-height: 140%;
        }
      }

      .correct {
        background: #55BFC7;
      }

      .wrong {
        background: var(--main-red);
      }

      .skipped {
        background: var(--main-light-shade-4);
      }

      .correct,
      .wrong,
      .skipped {
        font-size: 16px;
        border-radius: 8px;
        padding: var(--spacing-4) var(--spacing-16);
        min-width: 54px;
        text-align: center;
      }
    }

    .progress {
      width: 255px;
      outline: 3px solid rgba(20, 31, 49, 0.12);
      background: rgba(20, 31, 49, 0.12);
      border-radius: 50%;
      position: relative;
      z-index: 0;

      canvas {
        width: 255px;
      }

      &:after {
        content: '';
        background: var(--main-light);
        border-radius: 50%;
        position: absolute;
        width: 172px;
        height: 172px;
        top: 41px;
        left: 41px;
      }

      .number {
        position: absolute;
        z-index: 1;
        text-align: center;
        top: 86px;
        width: 100%;

        p {
          margin: 0;
          font-size: 18px;
          font-weight: 500;
        }

        p:first-of-type {
          font-size: 40px;
          margin-bottom: var(--spacing-8);
        }
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--spacing-16);

    .btn-text {
      padding: 0;
      color: var(--main-dark-light-3);
    }
  }
}

@include bp($bp-laptop) {
  .deck-progress {
    .holder-result {
      .title {
        font-size: 24px;
      }

      .answers {
        margin-right: var(--spacing-40);
      }

      .progress-holder {
        .progress {
          width: 240px;

          canvas {
            width: 240px;
          }

          &:after {
            width: 173px;
            height: 173px;
            top: 34px;
            left: 34px;
          }
        }

        .number {
          top: 76px;
        }
      }

      .result {
        &.checkpoint-result {
          max-width: 260px;

          .answers {
            margin-right: 0;
          }
        }

        .checkpoint {
          .subtitle {
            margin: var(--spacing-16) 0 var(--spacing-30);
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .deck-progress {
    height: 500px;
    padding: var(--spacing-50) var(--spacing-20);

    .holder-result {
      flex-direction: column;

      .result {
        max-width: unset;
        margin-right: 0;

        .your-result {
          margin-bottom: var(--spacing-12);
        }

        .checkpoint {
          .title {
            font-size: 24px;
          }
        }
      }

      .progress-holder {
        display: flex;
        justify-content: center;
        margin-bottom: var(--spacing-22);

        .progress {
          width: 200px;

          canvas {
            width: 200px;
          }

          &:after {
            width: 133px;
            height: 134px;
            top: 33px;
            left: 34px;
          }
        }

        .number {
          top: 60px;
        }
      }

      .answers {
        margin-right: 0;

        &.mobile {
          display: flex;
          justify-content: center;
          column-gap: 10px;

          .correct,
          .wrong,
          .skipped {
            padding: 2px var(--spacing-4);
            min-width: 40px;
            height: 22px;
            margin-top: var(--spacing-10);
          }

          div {
            flex-direction: column;
            align-items: center;
            text-align: center;
            width: 90px;
            margin-bottom: 0;

            span {
              font-size: 14px;

              &.known-cards {
                word-spacing: 9999px;
              }
            }
          }
        }

        &.desktop {
          display: none;
        }
      }
    }
  }
}
</style>
